import { useEffect, useState } from 'react';
import useGameStore, { valuationLiveOf } from '../store/gameStore';
import { money } from '../lib/format';
import { INVESTMENT_TIER_META } from '../types/gameTypes';
import type { InvestmentTier } from '../types/gameTypes';

/**
 * ⚖️ Insolvency Resolution Desk — raised when a HUMAN player's loan matures and their
 * cash cannot cover the amount due. The turn is paused: no silent auto-deduction, no
 * negative cash, no forced liquidation. The player covers the shortfall through:
 *   A) refinancing (new distress loan, credit-rating penalty),
 *   B) treasury fund withdrawals (early-exit penalty applies),
 *   C) a controlled asset sale (player picks exactly which shares to sell to the bank).
 * Once `cash >= amountDue` the primary CTA [Uhradit splátku a pokračovat] unlocks.
 */
const TIERS: InvestmentTier[] = ['SAVINGS', 'REIT', 'VENTURE'];

const InsolvencyModal = () => {
  const store = useGameStore();
  const insolvency = store.insolvency;
  const [picks, setPicks] = useState<Record<string, number>>({});

  useEffect(() => setPicks({}), [insolvency?.loanId]);
  if (!insolvency) return null;
  const player = store.players.find((p) => p.id === insolvency.playerId);
  if (!player) return null;

  const cash = Math.max(0, player.cash);
  const shortfall = Math.max(0, insolvency.amountDue - cash);
  const headroom = Math.max(0, store.debtCeilingOf(player.id) - player.debt - store.loanExposureOf(player.id));
  const canRefinance = headroom >= shortfall;
  const portfolioValue = store.portfolioValueOf(player.id);

  const holdings = store.board
    .map((tile) => ({ tile, owned: tile.shares[player.id] ?? 0 }))
    .filter((h) => h.owned > 0)
    .sort((a, b) => valuationLiveOf(a.tile) / a.tile.totalShares - valuationLiveOf(b.tile) / b.tile.totalShares);

  // Last resort: no credit headroom, no treasury funds and the portfolio cannot
  // cover the payment even after a full liquidation → true bankruptcy.
  const allExhausted = !canRefinance && store.investmentValueOf(player.id) <= 0 && portfolioValue < shortfall;

  const panel = 'rounded-xl border border-slate-700/60 bg-slate-950/60 p-3';

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/85 p-4 backdrop-blur-sm">
      <div className="w-full max-w-xl overflow-hidden rounded-2xl border border-rose-500/60 bg-slate-900 shadow-2xl">
        <div className="bg-rose-600/90 px-5 py-3">
          <p className="text-sm font-extrabold tracking-wide text-white">⚖️ Insolvenční oddělení — Resolution Desk</p>
          <p className="text-[11px] text-rose-100">
            {insolvency.playerName} · splatnost {insolvency.label}: požadováno {money(insolvency.amountDue)} · hotovost {money(cash)} ·
            deficit <b>{money(shortfall)}</b>
          </p>
        </div>

        <div className="max-h-[70vh] space-y-3 overflow-auto p-5">
          {/* ---- Option A: refinance ------------------------------------------------ */}
          <div className={`${panel} ${canRefinance ? 'border-amber-400/50' : 'opacity-50'}`}>
            <p className="text-xs font-bold uppercase tracking-widest text-amber-300">A · Refinancování / čerpání úvěru</p>
            <p className="mt-1 text-[11px] text-slate-400">
              Úvěrový strop: {money(headroom)} {canRefinance ? '· nová půjčka pokryje deficit' : '· nedostatečný na krytí deficitu'}.
              Rating klesne o 20 bodů, sankční úrok +3 %.
            </p>
            <button
              type="button"
              disabled={!canRefinance}
              onClick={() => store.insolvencyRefinance()}
              className="mt-2 w-full rounded-xl bg-gradient-to-r from-amber-400 to-orange-400 px-4 py-2 text-xs font-extrabold text-slate-950 disabled:from-slate-700 disabled:to-slate-700 disabled:text-slate-500"
            >
              Refinancovat novou půjčkou +{money(shortfall)}
            </button>
          </div>

          {/* ---- Option B: treasury withdrawals -------------------------------------- */}
          <div className={panel}>
            <p className="text-xs font-bold uppercase tracking-widest text-sky-300">B · Likvidace investičního portfolia</p>
            {TIERS.every((tier) => !store.investmentOf(player.id, tier)) ? (
              <p className="mt-1 text-[11px] text-slate-500">Žádné prostředky v pokladních fondech.</p>
            ) : (
              TIERS.map((tier) => {
                const entry = store.investmentOf(player.id, tier);
                if (!entry) return null;
                return (
                  <div key={tier} className="mt-2 flex items-center justify-between gap-2 text-xs">
                    <span className="text-slate-200">
                      {INVESTMENT_TIER_META[tier].name} · {money(entry.value)}
                    </span>
                    <button
                      type="button"
                      disabled={shortfall <= 0}
                      onClick={() => store.insolvencyWithdraw(tier)}
                      className="rounded-lg border border-sky-400/70 bg-sky-600/80 px-3 py-1 font-bold text-white hover:bg-sky-500 disabled:border-slate-700 disabled:bg-slate-800 disabled:text-slate-500"
                    >
                      Vybrat (kryje deficit {money(shortfall)})
                    </button>
                  </div>
                );
              })
            )}
          </div>

          {/* ---- Option C: controlled asset sale ------------------------------------ */}
          <div className={panel}>
            <p className="text-xs font-bold uppercase tracking-widest text-emerald-300">C · Řízený prodej aktiv (rozhoduješ ty)</p>
            <p className="mt-1 text-[11px] text-slate-400">Tržní hodnota zpětného odkupu bankou · 5% výstupní poplatek.</p>
            <div className="mt-2 max-h-48 space-y-1.5 overflow-auto pr-1">
              {holdings.map(({ tile, owned }) => {
                const picked = picks[tile.id] ?? 0;
                const unit = valuationLiveOf(tile) / Math.max(1, tile.totalShares);
                return (
                  <div key={tile.id} className="rounded-lg border border-slate-700/60 bg-slate-950/50 p-2 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-slate-100">{tile.name}</span>
                      <span className="text-slate-400">{owned} akcií · {money(unit)}/ak</span>
                    </div>
                    <div className="mt-1 flex items-center gap-2">
                      <button type="button" onClick={() => setPicks((p) => ({ ...p, [tile.id]: Math.max(0, picked - 5) }))} className="rounded-md bg-slate-800 px-2 py-0.5 font-bold text-slate-200">−5</button>
                      <input type="range" min={0} max={owned} value={picked} onChange={(e) => setPicks((p) => ({ ...p, [tile.id]: Number(e.target.value) }))} className="flex-1 accent-emerald-400" />
                      <button type="button" onClick={() => setPicks((p) => ({ ...p, [tile.id]: Math.min(owned, picked + 5) }))} className="rounded-md bg-slate-800 px-2 py-0.5 font-bold text-slate-200">+5</button>
                      <button
                        type="button"
                        disabled={picked <= 0}
                        onClick={() => {
                          store.insolvencySellShares(tile.id, picked);
                          setPicks((p) => ({ ...p, [tile.id]: 0 }));
                        }}
                        className="rounded-lg border border-emerald-400/70 bg-emerald-600/80 px-3 py-1 font-bold text-white hover:bg-emerald-500 disabled:border-slate-700 disabled:bg-slate-800 disabled:text-slate-500"
                      >
                        Prodat {picked} ({money(Math.round(picked * unit * 0.95))})
                      </button>
                    </div>
                  </div>
                );
              })}
              {holdings.length === 0 && <p className="text-[11px] text-slate-500">Žádné akcie k prodeji.</p>}
            </div>
          </div>

          {/* ---- Resolution CTA ----------------------------------------------------- */}
          <button
            type="button"
            disabled={player.cash < insolvency.amountDue}
            onClick={() => store.settleInsolvency()}
            className="w-full rounded-xl bg-gradient-to-r from-emerald-400 to-teal-300 px-4 py-2.5 text-sm font-extrabold text-slate-950 disabled:from-slate-700 disabled:to-slate-700 disabled:text-slate-500"
          >
            Uhradit splátku {money(insolvency.amountDue)} a pokračovat
          </button>
          <button
            type="button"
            disabled={!allExhausted}
            onClick={() => store.declareInsolvencyBankruptcy()}
            className="w-full rounded-xl bg-slate-800 px-4 py-2 text-xs font-semibold text-rose-300 disabled:text-slate-600"
          >
            Vyhlásit bankrot {allExhausted ? '(všechny možnosti vyčerpány)' : '(dostupné jen po vyčerpání všech možností)'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InsolvencyModal;
